import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

// Context Providers
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";

// Shared Components
import Navbar from "./components/shared/Navbar";

// Auth & Chat Components
import { AuthModal, AuthCallback } from "./components/auth";
import { WebSocketChat, ChatToggleButton } from "./components/chat";
import { PushNotificationPrompt } from "./components/notifications";
import { WatchlistPage } from "./components/user";
import { PaymentSuccess, PaymentCancel } from "./components/payments";

// Pages
import HomePage from "./pages/HomePage";
import LiveTVPage from "./pages/LiveTVPage";
import TVGuidePage from "./pages/TVGuidePage";
import ContentLibraryPage from "./pages/ContentLibraryPage";
import FreeMoviesPage from "./pages/FreeMoviesPage";
import PlutoGuidePage from "./pages/PlutoGuidePage";
import SubscriptionPage from "./pages/SubscriptionPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import SignUpModal from "./pages/SignUpModal";
import SearchModal from "./pages/SearchModal";

// =============================================================================
// APP
// =============================================================================

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showChat, setShowChat] = useState(false);
  const [activeChatRoom, setActiveChatRoom] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const hasSeenPrompt = localStorage.getItem('cbxtv_push_prompt_shown');
    if (!hasSeenPrompt && 'Notification' in window && Notification.permission === 'default') {
      const timer = setTimeout(() => {
        setShowPushPrompt(true);
        localStorage.setItem('cbxtv_push_prompt_shown', 'true');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleOpenAuth = (mode = 'login') => {
    setAuthMode(mode);
    setShowAuth(true);
  };

  return (
    <LanguageProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter 
              scrolled={scrolled}
              showSearch={showSearch}
              setShowSearch={setShowSearch}
              showSignUp={showSignUp}
              setShowSignUp={setShowSignUp}
              showAuth={showAuth}
              setShowAuth={setShowAuth}
              authMode={authMode}
              handleOpenAuth={handleOpenAuth}
              showChat={showChat}
              setShowChat={setShowChat}
              activeChatRoom={activeChatRoom}
              setActiveChatRoom={setActiveChatRoom}
              showPushPrompt={showPushPrompt}
              setShowPushPrompt={setShowPushPrompt}
            />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

function AppRouter({ 
  scrolled, showSearch, setShowSearch, showSignUp, setShowSignUp,
  showAuth, setShowAuth, authMode, handleOpenAuth,
  showChat, setShowChat, activeChatRoom, setActiveChatRoom,
  showPushPrompt, setShowPushPrompt
}) {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      
      {/* Auth Callback */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* My List */}
      <Route path="/my-list" element={
        <>
          <Navbar scrolled={scrolled} onSearchClick={() => setShowSearch(true)} onSignUpClick={() => setShowSignUp(true)} onOpenAuth={handleOpenAuth} />
          <WatchlistPage />
        </>
      } />
      
      {/* Public Routes */}
      <Route path="/*" element={
        <>
          <Navbar scrolled={scrolled} onSearchClick={() => setShowSearch(true)} onSignUpClick={() => setShowSignUp(true)} onOpenAuth={handleOpenAuth} />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/live-tv" element={<LiveTVPage />} />
            <Route path="/guide" element={<TVGuidePage />} />
            <Route path="/free-movies" element={<FreeMoviesPage />} />
            <Route path="/pluto-guide" element={<PlutoGuidePage />} />
            <Route path="/movies" element={<ContentLibraryPage />} />
            <Route path="/library" element={<ContentLibraryPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/cbxtra" element={<HomePage />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
          
          {showSearch && <SearchModal onClose={() => setShowSearch(false)} />}
          
          {showSignUp && (
            <SignUpModal onClose={() => setShowSignUp(false)} onSuccess={() => {}} />
          )}

          <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} initialMode={authMode} />

          {showChat && activeChatRoom && (
            <WebSocketChat roomId={activeChatRoom.id} roomName={activeChatRoom.name} isOpen={showChat} onClose={() => setShowChat(false)} />
          )}
          
          {!showChat && (
            <ChatToggleButton onClick={() => { setActiveChatRoom({ id: 'general', name: 'CBXTV Chat' }); setShowChat(true); }} />
          )}

          {showPushPrompt && <PushNotificationPrompt onClose={() => setShowPushPrompt(false)} />}
        </>
      } />
    </Routes>
  );
}

export default App;
